import React, { useState, useEffect } from 'react';
import CustomTable from '../../UI/CustomTable/CustomTable';
import { API } from '../../../assets/staticData';
import IconButton from '@mui/material/IconButton';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { tableQueryBuilder } from '../../../hooks/tableQueryBuilder';
import ApplicationDetailsList from './ApplicationDetailsList/ApplicationDetailsList';
import dayjs from "dayjs";
import { jobApplicationPdfGenerator } from './application-pdf-exporter';
import IosShareIcon from '@mui/icons-material/IosShare';

const EmploymentRequests = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState({
    pagination: { page: 1, pageSize: 10, total: 0 },
    query: {},
    sort: null,
    search: "",
  });

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [selectedDetails, setSelectedDetails] = useState(null);

  const userToken = localStorage.getItem("userToken");

  // Show details dialog
  const handleShow = async (row) => {
    setDetailsOpen(true);
    setDetailsLoading(true);
    setSelectedDetails(null);
    try {
      const res = await fetch(
        `${API}/job-applications/${row.id}?populate[questionsAndAnswers][populate]=attachments`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userToken}`,
          },
        }
      );
      const data = await res.json();
      setSelectedDetails(data.data);
    } catch (err) {
      setSelectedDetails({ error: "Error loading details." });
    }
    setDetailsLoading(false);
  };

  // Download PDF for a single application
  const handleDownload = async (row) => {
    try {
      const res = await fetch(
        `${API}/job-applications/${row.id}?populate[questionsAndAnswers][populate]=attachments`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userToken}`,
          },
        }
      );
      const data = await res.json();
      jobApplicationPdfGenerator(
        data.data,
        'https://mishov-blog.hel1.your-objectstorage.com'
      );
    } catch (err) {
      alert("Error generating PDF. Please try again.");
    }
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedDetails(null);
  };

  // Table columns, including Download and Show actions
  const columns = [
    { field: 'id', headerName: 'ID', flex: 0.5, minWidth: 60, searchable: false },
    { field: 'fullName', headerName: 'Full Name', flex: 1, minWidth: 120, searchable: true },
    { field: 'email', headerName: 'Email', flex: 1, minWidth: 150, searchable: true },
    { field: 'phone', headerName: 'Phone', flex: 0.8, minWidth: 100, searchable: true },
    {
      field: 'createdAt',
      headerName: 'Created At',
      flex: 0.8,
      minWidth: 120,
      searchable: false,
      renderCell: (params) => {
        const value = params.row.createdAt
        return value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-'
      },
    },
    {
      field: 'download',
      headerName: '',
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: 'center',
      renderCell: (params) => (
        <IconButton
          onClick={() => handleDownload(params.row)}
          aria-label="Download PDF"
          size="small"
        >
          <IosShareIcon fontSize="small" />
        </IconButton>
      ),
    },
    {
      field: 'show',
      headerName: '',
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: 'center',
      renderCell: (params) => (
        <IconButton
          onClick={() => handleShow(params.row)}
          aria-label="Show"
          size="small"
        >
          <VisibilityIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  const fetchData = async ({ page, pageSize, query, sort, search }) => {
    setLoading(true);

    const queryString = tableQueryBuilder({
      page,
      pageSize,
      sort,
      search,
      filters: query,
      columns,
    });

    const res = await fetch(
      `${API}/job-applications?${queryString}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userToken}`,
        },
      }
    );
    const data = await res.json();

    const tableRows = (data.data || []).map((item) => ({
      id: item.id,
      ...item.attributes,
    }));

    setRows(tableRows);
    setMeta({
      pagination: data.meta.pagination,
      query: query || {},
      sort: sort || null,
      search: search || "",
    });
    setLoading(false);
  };

  useEffect(() => {
    fetchData({
      page: meta.pagination.page,
      pageSize: meta.pagination.pageSize,
      query: meta.query,
      sort: meta.sort,
      search: meta.search,
    });
    // eslint-disable-next-line
  }, []);

  return (
    <div>
      <CustomTable
        rows={rows}
        columns={columns}
        loading={loading}
        meta={meta}
        onChange={fetchData}
      />

      <Dialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        maxWidth={false}
        PaperProps={{
          style: { width: 'fit-content', minWidth: 320 },
        }}
      >
        <DialogTitle>Application Details</DialogTitle>
        <DialogContent dividers>
          {detailsLoading && <CircularProgress />}
          {!detailsLoading && selectedDetails && !selectedDetails.error && (
            <ApplicationDetailsList details={selectedDetails} />
          )}
          {!detailsLoading && selectedDetails && selectedDetails.error && (
            <div style={{ color: 'red' }}>{selectedDetails.error}</div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default EmploymentRequests;
